import { useEffect, useState } from 'react';
import { Box, Drawer, PaperProps } from '@mui/material';

export interface SideSheetProps {
  open: boolean;
  onClose?: () => void;
  children?: React.ReactNode;
  width?: number | string;
  largeWidth?: number | string;
  PaperProps?: Partial<PaperProps<'div'>>;
  sx?: any;
  /**
   * SEE-780: opt-in to lazy keep-alive. When true, the panel content mounts
   * on first open and STAYS mounted thereafter (MUI's `Drawer keepMounted`
   * kicks in after first open), so opens 2..N are instant CSS toggles
   * instead of a fresh ~1 s mount of every form field, subscription, and
   * validator inside.
   *
   * Default `false` preserves MUI's default behavior: children unmount when
   * the drawer closes, and effects/subscriptions inside the panel die on
   * close. Only opt in for panels whose effects either gate on the `open`
   * prop or are tied to an entity ID that's independently cleaned up — NOT
   * panels that rely on unmount to tear down resources (subscriptions,
   * intervals, polling, GraphQL watchers). When `eagerMount` is true,
   * `keepMounted` is implicitly true from the first render.
   */
  keepMounted?: boolean;
  /**
   * SEE-780: pre-mount the panel content before the first open. Use when
   * the panel is almost guaranteed to be opened (e.g., a primary action)
   * and the extra ms on initial render is worth a fully-instant first open.
   * Implies `keepMounted=true`.
   */
  eagerMount?: boolean;
}

function SideSheet({
  open = false,
  onClose,
  children,
  width,
  largeWidth,
  PaperProps,
  sx,
  keepMounted = false,
  eagerMount = false,
}: SideSheetProps) {
  // Lazy keep-alive: track whether the panel has been opened at least once,
  // and pass keepMounted=true to MUI Drawer after that. With eagerMount, the
  // initial state is already true (panel mounts immediately).
  const [hasOpenedOnce, setHasOpenedOnce] = useState(eagerMount);
  useEffect(() => {
    if (open && !hasOpenedOnce) {
      setHasOpenedOnce(true);
    }
  }, [open, hasOpenedOnce]);

  // MUI keepMounted is only meaningful when the caller has opted in AND the
  // panel has been opened at least once (lazy keep-alive). Otherwise leave
  // it unset and let MUI use its default behavior (unmount on close).
  const drawerKeepMounted = keepMounted && hasOpenedOnce;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={PaperProps}
      keepMounted={drawerKeepMounted}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: { xs: '100vw', md: width, lg: largeWidth },
          height: '100%',
          ...sx,
        }}
      >
        {children}
      </Box>
    </Drawer>
  );
}

export default SideSheet;
